import { Component, Input, OnDestroy, OnInit } from "@angular/core";
import { VoyageReportSorted } from "@app/services/api/api.models";
import { ReportsService } from "@app/services/reports/reports.service";
import { Subscription } from "rxjs";
import { Chart, registerables } from "chart.js";
import { ApiService } from "@app/services/api/api.service";
import { VesselGroupsService } from "@app/services/vessel-groups/vessel-groups.service";
import { VoyageService } from "@app/services/voyages/voyage.service";
import { DateRange } from "@app/helpers/ranges";
import { VoyageRangeService } from "@app/services/ranges/voyage-range.service";
import { DecimalPipe } from "@angular/common";

@Component({
  selector: "app-voyage-reports",
  templateUrl: "./voyage-reports.component.html",
  styleUrls: ["./voyage-reports.component.scss"],
})
export class VoyageReportsComponent implements OnInit, OnDestroy {
  @Input() reportsService: ReportsService;

  selectedVessel: any;
  filteredVoyageReports: VoyageReportSorted[] | undefined = undefined;
  weatherAtSeaValues: any;
  ciiRatingValues: any;
  reportAndEventsValues: any;
  allVoyageReports: VoyageReportSorted[] = [];

  datePickerSelected: DateRange = undefined;

  private subscriptions: Subscription[] = [];

  constructor(
    private apiService: ApiService,
    private vesselGroupsService: VesselGroupsService,
    private voyageService: VoyageService,
    private voyageRangeService: VoyageRangeService,
    private decimalPipe: DecimalPipe
  ) {}

  ngOnInit(): void {
    Chart.register(...registerables);
    this.apiService
      .initialize()
      .toPromise()
      .then(() => {
        this.subscriptions.push(
          this.vesselGroupsService.getSelectedVesselObservable().subscribe({
            next: (selectedVessel) => {
              this.selectedVessel = selectedVessel;
            },
          }),
          this.voyageRangeService.getRangeSubject().subscribe({
            next: (dateRange) => {
              this.datePickerSelected = dateRange;
            },
          }),
          this.voyageService.initialize().subscribe({
            next: (voyages) => {
              if (!voyages) {
                this.allVoyageReports = undefined;
                this.weatherAtSeaValues = undefined;
                this.ciiRatingValues = undefined;
                this.reportAndEventsValues = undefined;
                return;
              }

              this.allVoyageReports = voyages.map((voyage) => {
                const month = new Date(voyage.departureAt);
                month.setUTCDate(1);
                month.setUTCHours(0, 0, 0, 0);
                return {
                  monthYear: month,
                  voyageReportData: voyage,
                  voyageReportTFCRData: {
                    totalFuelConsumptionMain_t: voyage.totalFuelConsumptionMain_t,
                    totalFuelConsumptionAux_t: voyage.totalFuelConsumptionAux_t,
                    totalFuelConsumptionBoilers_t: voyage.totalFuelConsumptionBoilers_t,
                  },
                  voyageReportWeatherData: {
                    weatherAtSeaGood_ratio: voyage.weatherAtSeaGood_ratio,
                    weatherAtSeaBad_ratio: voyage.weatherAtSeaBad_ratio,
                  },
                  voyageReportMapData: {
                    departureAt: voyage.departureAt,
                    departurePort: voyage.departurePort,
                    departurePortLon: voyage.departurePortLon,
                    departurePortLat: voyage.departurePortLat,
                    arrivalAt: voyage.arrivalAt,
                    arrivalPort: voyage.arrivalPort,
                    arrivalPortLon: voyage.arrivalPortLon,
                    arrivalPortLat: voyage.arrivalPortLat,
                  },
                  voyageReportCiiRatingData: {
                    ciiRatingBefore: voyage.ciiRatingBefore,
                    aerBefore_gtnm: voyage.aerBefore_gtnm,
                    ciiRatingAfter: voyage.ciiRatingAfter,
                    aerAfter_gtnm: voyage.aerAfter_gtnm,
                    eeio_gtnm: voyage.eeio_gtnm,
                    ciiRanges: voyage.ciiRanges,
                  },
                  voyageReportReportAndEventsData: {
                    reportAndEvents: voyage.reportAndEvents,
                  },
                };
              });

              this.allVoyageReports.sort(
                (first, second) => new Date(first.voyageReportMapData.departureAt).getTime() - new Date(second.voyageReportMapData.departureAt).getTime()
              );

              this.filteredVoyageReports = this.filterVoyagesByDateRange(this.allVoyageReports, this.datePickerSelected);

              this.weatherAtSeaValues = voyages.map((voyage) => ({
                good: Number((voyage.weatherAtSeaGood_ratio * 100).toFixed(2)),
                bad: Number((voyage.weatherAtSeaBad_ratio * 100).toFixed(2)),
              }));

              this.ciiRatingValues = voyages.map((voyage) => ({
                mainEngines: voyage.totalFuelConsumptionMain_t,
                auxEngines: voyage.totalFuelConsumptionAux_t,
                boilers: voyage.totalFuelConsumptionBoilers_t,
              }));

              this.reportAndEventsValues = voyages.map((voyage) => voyage.reportAndEvents);
            },
          })
        );
      });
  }

  ngOnDestroy(): void {
    for (const subscription of this.subscriptions) {
      subscription.unsubscribe();
    }
  }

  getRangeService() {
    return this.voyageRangeService;
  }

  isLoading(): boolean {
    return this.allVoyageReports === undefined;
  }

  getSelectedVesselId(): number {
    return this.vesselGroupsService.getSelectedVesselId();
  }

  getVesselName() {
    return this.selectedVessel ? this.selectedVessel.name : "";
  }

  onDateRangeChanged(dateRange: DateRange) {
    this.voyageRangeService.setRange(dateRange);
    this.filteredVoyageReports = this.filterVoyagesByDateRange(this.allVoyageReports, dateRange);
  }

  valueOrNV(value: number, format: string, unit: string) {
    return value != null ? this.decimalPipe.transform(value, format) + " " + unit : "NV";
  }

  private filterVoyagesByDateRange(voyages: VoyageReportSorted[], range: DateRange): VoyageReportSorted[] {
    if (!range?.start || !range?.end) return voyages;

    const start = new Date(range.start).getTime();
    const end = new Date(range.end).getTime();

    return voyages.filter((report) => {
      const departure = new Date(report.voyageReportData.departureAt).getTime() / 1000;
      const arrival = new Date(report.voyageReportData.arrivalAt ?? new Date()).getTime() / 1000;
      return departure <= end && arrival >= start;
    });
  }
}
